import type { Guide } from "./types.js";
import { Lead, H2, P, Ul, Li, AppLink, C, Callout } from "./primitives.js";

export const meta = {
  id: "asset-storage",
  title: "Where assets are stored",
  category: "Reference",
  lastUpdated: "2026-07-11",
  description: "Sanity + Google Drive: how generated images/videos/markdown get from your laptop to a public URL the distribution layer can use.",
  relatedGuides: ["produce-article", "distribute-article", "credentials-reference", "regenerate-article"],
  readTimeMin: 5,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        Every image, video, and article markdown the pipeline produces ends up in three places: local disk (always), Sanity (primary — public URL for distribution), Google Drive (team-browsable mirror). Distribution channels read public URLs from Sanity via the <C>keyword_assets</C> SQLite table — no more pasting image URLs into the IG dropdown.
      </Lead>

      <H2>The three destinations</H2>
      <Ul>
        <Li>
          <strong>Local FS</strong> — under <C>data/images/&lt;slug&gt;/</C> and <C>data/videos/&lt;slug&gt;/</C>. Always written. Source of truth for re-runs (skip-existing checks).
        </Li>
        <Li>
          <strong>Sanity</strong> — primary storage. Every generated asset is uploaded to Sanity's CDN at generation time. The CDN URL goes into <C>keyword_assets.sanity_url</C> and that's what IG / FB / X fetch at distribute time. <strong>Requires a SANITY_API_TOKEN with EDITOR permissions</strong> — the read-only token used by the Content DB sync isn't enough for uploads.
        </Li>
        <Li>
          <strong>Google Drive</strong> — team-browsable mirror. Files land at <C>&lt;parent&gt;/&lt;keyword-slug&gt;/&#123;images,videos,article&#125;/&lt;file&gt;</C> — mirrors the local <C>data/</C> layout so humans can find specific assets at a glance. Failures here NEVER block an article — Drive is convenience only.
        </Li>
      </Ul>

      <Callout kind="tip">
        Sanity is the load-bearing one. If you skip Drive entirely, distribution still works. If you skip Sanity, IG/FB/X distribute calls fall back to "you must paste an image URL" mode.
      </Callout>

      <H2>What gets uploaded</H2>
      <Ul>
        <Li><strong>hero</strong> — main banner image. Used by FB photo, IG feed, Brevo email hero.</Li>
        <Li><strong>in_content</strong> — section images from <C>&#123;&#123;IMAGE: ...&#125;&#125;</C> markers. Stay inside the article markdown.</Li>
        <Li><strong>social_card</strong> — 1200×630 OG share image with H1 overlay. Used by FB link posts + Twitter cards.</Li>
        <Li><strong>thumbnail</strong> — YouTube thumb with H1 overlay (only when <C>--thumbnail</C> is passed).</Li>
        <Li><strong>short_vertical</strong> — 9:16 clip for Reels/Shorts/TikTok.</Li>
        <Li><strong>square_feed</strong> / <strong>long_horizontal</strong> — IG feed loop / YouTube long-form (optional).</Li>
        <Li><strong>article_markdown</strong> — produced article body as a <C>.md</C> file. Drive only — Sanity stores articles as documents, not assets.</Li>
      </Ul>

      <H2>How it flows</H2>
      <Ul>
        <Li><strong>Generate</strong> — <C>keywords images generate &lt;id&gt;</C> (or videos) writes the file to disk (PNG / MP4).</Li>
        <Li><strong>Convert (images only)</strong> — sibling <C>.webp</C> is created via sharp at quality 80. Standard hero (1200×630) lands under ~200KB vs ~1.5MB for the PNG. Original PNG stays on disk for debugging.</Li>
        <Li><strong>Upload</strong> — orchestrator uploads the optimised file (WebP for images, MP4 for videos, MD for article) to Sanity, then Drive in the asset-type subfolder. Both happen in-process; no separate queue.</Li>
        <Li><strong>Record</strong> — one row per upload in <C>keyword_assets</C> with status (uploaded / partial / failed), Sanity URL, Drive view URL.</Li>
        <Li><strong>Distribute</strong> — IG/FB/X read <C>keyword_assets.sanity_url</C> filtered by (keyword_id, kind). No public-URL paste needed.</Li>
      </Ul>

      <H2>Filenames (SEO + LLM comprehension)</H2>
      <P>
        Every image filename leads with the primary-keyword slug — <C>reflexology-labor-induction-hero.webp</C>, <C>reflexology-labor-induction-what-expect-treatment.webp</C>, <C>reflexology-labor-induction-scene-00.webp</C> — instead of generic <C>hero.webp</C> / <C>scene-00.webp</C>. This makes the Sanity Media Library self-describing and helps any tool or AI agent understand an asset in isolation. In-content section images are prefixed too (deduped so the keyword isn't repeated, capped at ~80 chars).
      </P>
      <Callout kind="info">
        Honest caveat: on the current Sanity CDN, the served image URL is a content <em>hash</em> (<C>cdn.sanity.io/images/.../7bdca0c…-1600x900.webp</C>), so the filename is <strong>not</strong> in the public URL — it won't directly move Google Images rankings. The real image-SEO lever is the <strong>alt text</strong>, which the pipeline already rewrites to include the keyword. The filename win is Sanity's stored <C>originalFilename</C>, operator/LLM comprehension, and future-proofing a filesystem-served CDN (where the path <em>is</em> the URL).
      </Callout>

      <H2>WebP conversion</H2>
      <P>
        Every image (hero, in-content, social, thumbnail) is converted to WebP before upload using <C>sharp</C> at quality 80. Sibling file pattern — the original <C>hero.png</C> stays in <C>data/images/&lt;slug&gt;/</C> for local debugging, and a fresh <C>hero.webp</C> is written next to it. The .webp is what lands in both Sanity and Drive.
      </P>
      <Ul>
        <Li><strong>Expected sizes</strong> at standard resolution: hero 1200×630 ~150-200KB, in-content 1024×1024 ~100-150KB, social card 1200×630 ~150-200KB, thumbnail 1280×720 ~150-200KB.</Li>
        <Li><strong>Quality</strong>: 80 is the visually-indistinguishable-from-PNG default. Override via the <C>webpQuality</C> opt on uploadKeywordAsset (lower for smaller; 60 is the lower bound before banding shows on gradients).</Li>
        <Li><strong>Idempotent</strong>: if the .webp sibling exists and is newer than the PNG, conversion is skipped. Re-runs of <C>images generate</C> after a manual PNG edit re-convert automatically.</Li>
        <Li><strong>Opt out</strong>: pass <C>convertImagesToWebp: false</C> to upload the PNG as-is. Videos + markdown are uploaded as-is regardless.</Li>
      </Ul>

      <H2>Setup checklist</H2>
      <Ul>
        <Li>
          <strong>Sanity</strong>: open <AppLink to="credentials">Credentials</AppLink> → Sanity card → ensure <C>SANITY_API_TOKEN</C> has <strong>Editor</strong> permissions (not Viewer). Regenerate at sanity.io/manage if needed. Test should still pass — the upload-permission check happens at first upload, not at Test.
        </Li>
        <Li>
          <strong>Google Drive</strong>: open <AppLink to="credentials">Credentials</AppLink> → Google Drive card. Click "Setup instructions" — it walks you through the 4-step flow (Cloud project → OAuth consent → Playground for refresh token → paste here + parent folder ID). ~10 min one-time. After that, the refresh token is valid until you revoke it.
        </Li>
        <Li>
          <strong>That's it</strong>. Every new <C>images generate</C> / <C>videos generate</C> uploads automatically. Re-runs only upload NEW outputs — skip-existing files don't re-upload.
        </Li>
      </Ul>

      <H2>Cost + storage</H2>
      <Ul>
        <Li><strong>Sanity</strong>: included in your existing plan. Per-asset storage is tiny (KB-MB per image, &lt;30MB per video).</Li>
        <Li><strong>Drive</strong>: 15GB free tier holds 100s of articles. Workspace upgrade if/when you fill it.</Li>
        <Li><strong>No bandwidth surprises</strong> — Sanity CDN serves images at viewer-load time; Drive is just for the team.</Li>
      </Ul>

      <H2>Failure modes</H2>
      <Ul>
        <Li>
          <strong>"Sanity asset upload denied — token needs EDITOR permissions"</strong>: the read-only Content DB token doesn't have write scope. Generate a new token at sanity.io/manage with Editor, paste into the Sanity card.
        </Li>
        <Li>
          <strong>"Drive refresh-token exchange failed"</strong>: refresh token was revoked (likely via the OAuth consent screen) or the OAuth client was deleted. Re-do the Playground flow.
        </Li>
        <Li>
          <strong>"parent folder isn't accessible"</strong>: the OAuth user doesn't have access to the folder ID you pasted, or the folder was deleted. Share the folder with the authenticated user, or clear the field to upload to Drive root.
        </Li>
        <Li>
          <strong>Partial uploads</strong>: <C>status: partial</C> on a row means one destination succeeded and one didn't. The article still distributes (Sanity URL is present). The Drive copy can be retried by re-running the image-gen command.
        </Li>
      </Ul>

      <Callout kind="tip">
        The asset table is queryable directly: <C>sqlite3 data/keywords/keywords.db "SELECT kind, status, sanity_url FROM keyword_assets WHERE keyword_id = 'kw-...'"</C>. Use this when debugging why an IG post can't find a hero image.
      </Callout>
    </>
  );
}

export const guide: Guide = { meta, Body };
